import clsx from "clsx";
import { WarehouseElementLocation } from "components/WarehouseElements/WarehouseElements";
import {
  EMPLACEMENT_TYPE,
  EMPTY_DIAMOND_CLASSNAME,
  FIXE_TYPE,
  TEXTE_TYPE,
  PARK_TYPE,
} from "constants/globals";
import { TestIds } from "constants/Tests/TestIds";
import { useAppDispatch, useAppSelector } from "hooks/StoreHooks";
import React, { forwardRef, useEffect, useMemo } from "react";
import { getZoomLevel } from "store/Selectors/gridLayoutSelector";
import { selectIsOccupied } from "store/Selectors/StatusSelectors/isOccupiedSelector";
import { setOpenedDetailId } from "store/Slices/gridEventSlice";
import {
  selectHasPallet,
  selectHasCage,
  selectHasInventory,
  selectHasStacks,
} from "store/Selectors/StatusSelectors/contentSelector";
import "./scss/InteractiveGridElement.scss";
import { selectIsOperationInProgress } from "store/Selectors/StatusSelectors/isOperationInProgressSelector";
import { selectReservationType } from "store/Selectors/StatusSelectors/reservationTypeSelector";
import { selectStatusById } from "store/Selectors/StatusSelectors/statusSelector";
import { getWarehousePlanDimensions } from "store/Selectors/warehousePlanSelector";

export type BorderOverrideProps = {
  removeTopBorder?: boolean;
  removeBottomBorder?: boolean;
  removeLeftBorder?: boolean;
  removeRightBorder?: boolean;
};

type Props = {
  type: string;
  id: string;
  gridElementLocationSpan: WarehouseElementLocation;
  borderOverrides?: BorderOverrideProps;
  mainGridLocation: WarehouseElementLocation;
};

export const INTERACTIVE_CLASSNAME_SUFFIX = "-interactive";
const SEARCHED_CLASSNAME = "searched";

const InteractiveGridElement = forwardRef<HTMLDivElement, Props>(
  (
    {
      id,
      type,
      gridElementLocationSpan: location,
      borderOverrides,
      mainGridLocation,
    },
    ref
  ) => {
    const isOccupied = useAppSelector(selectIsOccupied(id));
    const hasStacks = useAppSelector(selectHasStacks(id));
    const hasPallet = useAppSelector(selectHasPallet(id));
    const hasCage = useAppSelector(selectHasCage(id));
    const hasInventory = useAppSelector(selectHasInventory(id));
    const isOperationInProgress = useAppSelector(
      selectIsOperationInProgress(id)
    );

    const isElementSearched = useAppSelector(
      state =>
        state.gridEventSlice.searchedItemId?.toLowerCase() === id.toLowerCase()
    );

    const elementSearchedRefreshToken = useAppSelector(state => {
      if (isElementSearched)
        return state.gridEventSlice.searchedItemRefreshToken;
      else return 0;
    });

    const reservationType = useAppSelector(selectReservationType(id));
    const status: string = useAppSelector(selectStatusById(id));
    const zoomLevel = useAppSelector(getZoomLevel);
    const originalType = type;
    const lowerCaseType = type.toLowerCase();
    const isMyWindowOpen = useAppSelector(
      state => state.gridEventSlice.openedDetailId === id
    );

    const dispatch = useAppDispatch();

    const handleCellClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      const nextId = [PARK_TYPE, EMPLACEMENT_TYPE].includes(type)
        ? isMyWindowOpen
          ? null
          : id
        : null;
      dispatch(setOpenedDetailId(nextId));
    };

    const className = useMemo(
      () =>
        createInteractiveGridElementClassName(
          lowerCaseType,
          isOperationInProgress,
          isOccupied,
          hasStacks,
          hasPallet,
          hasCage,
          hasInventory,
          status,
          reservationType,
          isElementSearched,
          borderOverrides
        ),
      [
        lowerCaseType,
        isOperationInProgress,
        isOccupied,
        hasStacks,
        hasPallet,
        hasCage,
        hasInventory,
        status,
        reservationType,
        isElementSearched,
        borderOverrides,
      ]
    );

    function resetAnimation() {
      const interactiveElements = document.getElementsByClassName(
        id + INTERACTIVE_CLASSNAME_SUFFIX
      );
      if (interactiveElements.length > 0) {
        for (let i = 0; i < interactiveElements.length; i++) {
          interactiveElements[i].classList.remove(SEARCHED_CLASSNAME);
        }
        setTimeout(() => {
          for (let i = 0; i < interactiveElements.length; i++) {
            interactiveElements[i].classList.add(SEARCHED_CLASSNAME);
          }
        }, 4);
      }
    }
    useEffect(() => {
      if (isElementSearched) {
        resetAnimation();
      }
    }, [elementSearchedRefreshToken]);

    const mainGridOffset: WarehouseElementLocation = {
      start: {
        x: mainGridLocation.start.x,
        y: mainGridLocation.start.y,
      },
      end: {
        x: mainGridLocation.end?.x ?? 0,
        y: mainGridLocation.end?.y ?? 0,
      },
    };

    const planStartDimensions = useAppSelector(getWarehousePlanDimensions);

    const offset =
      originalType === PARK_TYPE &&
      (location.start.x > (planStartDimensions?.start.x ?? 1) ||
        location.start.y > (planStartDimensions?.start.y ?? 1))
        ? 2
        : 1;
    const gridColumnStart = location.start.x - mainGridOffset.start.x + offset;
    const gridRowStart = location.start.y - mainGridOffset.start.y + offset;
    const gridColumnEnd = location.end
      ? `span ${Math.abs(location.end.x - location.start.x + 1)}`
      : gridColumnStart;
    const gridRowEnd = location.end
      ? `span ${Math.abs(location.end.y - location.start.y + 1)}`
      : gridRowStart;

    return (
      <div
        ref={ref}
        key={`${id}-${location.start.x}-${location.start.y}`}
        data-testid={TestIds.INTERACTIVE}
        className={`${className} ${id + INTERACTIVE_CLASSNAME_SUFFIX}`}
        style={{
          gridColumnStart,
          gridColumnEnd,
          gridRowStart,
          gridRowEnd,
        }}
        {...(lowerCaseType == EMPLACEMENT_TYPE
          ? { onClick: handleCellClick }
          : {})}>
        {hasStacks || hasCage || hasPallet ? (
          <div
            data-testid={TestIds.INNER_DIAMOND}
            className={EMPTY_DIAMOND_CLASSNAME}></div>
        ) : null}
        {createOperationRedDot(isOperationInProgress, originalType, zoomLevel)}
      </div>
    );
  }
);

InteractiveGridElement.displayName = "InteractiveGridElement";
export default InteractiveGridElement;

function createOperationRedDot(
  isOperationInProgress: boolean,
  type: string,
  zoomLevel: number
): React.ReactNode {
  if (isOperationInProgress && type === EMPLACEMENT_TYPE) {
    return (
      <div
        className="operation-marker"
        style={{
          width: `${(zoomLevel / 100) * 0.8}rem`,
          height: `${(zoomLevel / 100) * 0.8}rem`,
        }}
      />
    );
  }
  return null;
}

function createInteractiveGridElementClassName(
  type: string,
  isOperationInProgress: boolean,
  isOccupied: boolean,
  hasStacks: boolean,
  hasPallet: boolean,
  hasCage: boolean,
  hasInventory: boolean,
  status: string,
  reservationType: string,
  isElementSearched: boolean,
  borderOverrides?: BorderOverrideProps
): string {
  const inventoryClassName = getInventoryClassName(
    isOccupied,
    hasStacks,
    hasPallet,
    hasCage,
    hasInventory
  );
  return clsx(
    "unselectable-text",
    type === PARK_TYPE || type === FIXE_TYPE || type === TEXTE_TYPE
      ? "non-interactive-element"
      : "interactive-element",
    type,
    typeof status === "string" && status !== "INVALIDE" && status,
    inventoryClassName,
    isOperationInProgress && "operation-in-progress",
    status === "RSV" && reservationType && `rsvt-${reservationType}`,
    borderOverrides?.removeTopBorder && "no-top-border",
    borderOverrides?.removeBottomBorder && "no-bottom-border",
    borderOverrides?.removeLeftBorder && "no-left-border",
    borderOverrides?.removeRightBorder && "no-right-border",
    isElementSearched && SEARCHED_CLASSNAME
  );
}

function getInventoryClassName(
  isOccupied: boolean,
  hasStacks: boolean,
  hasPallet: boolean,
  hasCage: boolean,
  hasInventory: boolean
): string {
  let inventoryClassName = "";
  if (isOccupied) {
    inventoryClassName += "OCC ";
    if (hasInventory) {
      inventoryClassName += "inventory ";
    }
    if (hasStacks) {
      inventoryClassName += "stacks ";
    }
    if (hasCage) {
      inventoryClassName += "cage ";
    }
    if (hasPallet) {
      inventoryClassName += "pallet";
    }
    inventoryClassName = inventoryClassName.trim();
  }
  return inventoryClassName;
}
